import React, { FC, ReactNode, useRef } from 'react';

import { AlertDialog, Button } from '@rbilabs/universal-components';

import { LogUserErrorSeverity, useGetAndLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';

import CheckoutErrorCell from './checkout-error-cell';
import { StyledHeader } from './styled';

interface ICheckoutErrorsModalProps {
  checkoutErrors: Array<{ messageId: TranslationKey; message: string | ReactNode }>;
  onClose: () => void;
  onCtaPress?: () => void;
  ctaText?: string;
  heading?: string;
}

export const CheckoutErrorsModal: FC<React.PropsWithChildren<ICheckoutErrorsModalProps>> = ({
  checkoutErrors,
  onClose,
  onCtaPress,
  ctaText,
  heading,
}) => {
  const cancelRef = useRef(null);

  const hasMoreThanOneError = checkoutErrors.length > 1;

  const errorsToBeDisplayed = checkoutErrors.map(({ messageId, message }) => (
    <CheckoutErrorCell
      key={messageId}
      showDot={hasMoreThanOneError}
      checkoutErrorId={messageId}
      message={message}
    />
  ));

  const getUnableToPlaceOrderMessage = useGetAndLogUserErrorMessage({
    message: heading ?? t('unableToProcessOrder'),
    severity: LogUserErrorSeverity.Recoverable, // likely recoverable but could be irrecoverable
    context: { moreInfo: 'checkout-errors-modal.tsx' },
  });

  return (
    <AlertDialog
      // Blaine:
      // there is a bug, where if we pass this ref to the Button below,
      // the app crashes in react-router trying to find the react-navigation context
      // this makes zero sense. But I've found that if we just dont attach the
      // ref below then things don't crash :)
      leastDestructiveRef={cancelRef}
      isOpen={true}
      onClose={onClose}
      testID="checkout-errors-dialog"
      closeOnOverlayClick={true}
    >
      <AlertDialog.Content>
        <AlertDialog.Header>
          <StyledHeader variant="headerTwo">{getUnableToPlaceOrderMessage()}</StyledHeader>
        </AlertDialog.Header>
        <AlertDialog.Body alignSelf={'center'}>{errorsToBeDisplayed}</AlertDialog.Body>
        <AlertDialog.Footer>
          <Button
            {...(!onCtaPress && { variant: 'outline' })}
            width="full"
            onPress={onCtaPress ?? onClose}
            textTransform={'capitalize'}
          >
            {ctaText ?? t('okay')}
          </Button>
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
};
